import { Page_settings } from "@/config/page_settings";
import React, { useContext, useMemo, useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";

export type AuthCodeModalProps = {
    onClose: () => void;
    onResend?: () => void;
    title?: string;
    description?: string;
  };
  
  const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
  `;
  
  const popIn = keyframes`
    from { transform: translateY(4px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  `;
  
  
  
  const pad2 = (n: number) => String(n).padStart(2, "0");
  
  const splitChars = (code?: string) => (code ? code.split("") : []);
  
  const Backdrop: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <Overlay onClick={onClick} />
  );
  
  const DeviceCreateModal: React.FC<AuthCodeModalProps> = ({
    onClose,
    onResend,
    title = "인증번호",
    description = "아래 번호를 입력창에 그대로 입력하세요.",
  }) => {

  
  /* **********************************
   * GLOBAL STATE
   ********************************** */
  const context: any = useContext(Page_settings);
  
  const [copied, setCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  
  const { id } = useParams();

  /* **********************************
   * LOCAL STATE
   ********************************** */
  const [code, setCode] = useState("");
  const boxes = useMemo(() => splitChars(code), [code]);
  const [storeId, setStoreId] = useState(0);


  /* **********************************
   * Handlers
   ********************************** */
  const getCode = () => {
    context.post("/office/createDeviceEnrollCode",{
    storeId,
    ttlSeconds: 600
  }, (response) => {
    setCode(response.enrollmentCode);
  });
};


  /* **********************************
   * EFFECTS
   * 컴포넌트 상태 변경 처리
   ********************************** */
  
    useEffect(() => {
      setStoreId(parseInt(id ?? "0"));
    }, [id]);
  
    useEffect(() => {
      if (storeId === 0) {
        return;
      }

      getCode();
    },[storeId])

    // ESC 닫기 + 포커스 트랩 + 스크롤 잠금
    useEffect(() => {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
  
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose();
        if (e.key === "Tab" && modalRef.current) {
          const focusables = modalRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusables.length === 0) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;
          if (e.shiftKey) {
            if (active === first) {
              last.focus();
              e.preventDefault();
            }
          } else {
            if (active === last) {
              first.focus();
              e.preventDefault();
            }
          }
        }
      };
  
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = prevOverflow;
      };
    }, [onClose]);
  
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      } catch {}
    };

  
    return (
      <Dialog aria-live="polite">
        <Backdrop onClick={onClose} />
        <Panel
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="auth-modal-title"
          aria-describedby="auth-modal-desc"
        >
          <Header>
            <div>
              <Title id="auth-modal-title">{title}</Title>
              <Desc id="auth-modal-desc">{description}</Desc>
            </div>
            <CloseButton onClick={onClose} aria-label="닫기">
              <span aria-hidden>×</span>
            </CloseButton>
          </Header>
  
          <CodeWrap>
            {boxes.length > 0 ? (
              <CodeRow>
                {boxes.map((ch, idx) => (
                  <CodeBox key={`${ch}-${idx}`}>{ch}</CodeBox>
                ))}
              </CodeRow>
            ) : (
              <DashedBox>코드가 아직 없습니다.</DashedBox>
            )}
          </CodeWrap>
  
          <Footer>
            <Timer>
            </Timer>
            <Actions>
              <Btn onClick={handleCopy} disabled={!code} variant="outline">
                {copied ? "복사됨" : "복사"}
              </Btn>
              {onResend && (
                <Btn onClick={onResend} variant="primary">재전송</Btn>
              )}
            </Actions>
          </Footer>
        </Panel>
      </Dialog>
    );
  };
  
  export default DeviceCreateModal;

  const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    -webkit-backdrop-filter: blur(1px);
    backdrop-filter: blur(1px);
    animation: ${fadeIn} 120ms ease-out;
    z-index: 0;
  `;
  
  const Dialog = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
  `;
  
  const Panel = styled.div`
    width: 100%;
    max-width: 640px;
    margin: 16px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
    border: 1px solid rgba(0,0,0,0.06);
    padding: 24px;
    animation: ${popIn} 160ms ease-out;
    position: relative;
    z-index:1;
  `;
  
  const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  `;
  
  const Title = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #111827; /* gray-900 */
  `;
  
  const Desc = styled.p`
    margin: 6px 0 0 0;
    font-size: 14px;
    color: #6b7280; /* gray-500 */
  `;
  
  const CloseButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e5e7eb; /* gray-200 */
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    color: #4b5563; /* gray-600 */
    background: #fff;
    cursor: pointer;
    transition: background 0.15s ease;
    &:hover { background: #f9fafb; }
    &:focus { outline: 2px solid #d1d5db; outline-offset: 2px; }
  `;
  
  const CodeWrap = styled.div`
    margin-top: 16px;
    user-select: all;
  `;
  
  const CodeRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  `;
  
  const CodeBox = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 56px;
    border-radius: 12px;
    background: #f9fafb; /* gray-50 */
    border: 1px solid #e5e7eb; /* gray-200 */
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    letter-spacing: 0.2em;
  `;
  
  const DashedBox = styled.div`
    text-align: center;
    border: 1px dashed #d1d5db;
    background: #f9fafb;
    color: #6b7280;
    border-radius: 12px;
    padding: 16px;
    font-size: 14px;
  `;
  
  const Footer = styled.div`
    margin-top: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;
  
  const Timer = styled.div`
    font-size: 14px;
    color: #4b5563; /* gray-600 */
    b { font-variant-numeric: tabular-nums; }
  `;
  
  const Btn = styled.button<{variant?: 'primary' | 'outline'}>`
    appearance: none;
    border-radius: 12px;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.15s ease, background 0.15s ease;
    border: ${({ variant }) => variant === 'outline' ? '1px solid #e5e7eb' : 'none'};
    color: ${({ variant }) => variant === 'primary' ? '#fff' : '#111827'};
    background: ${({ variant }) => variant === 'primary' ? '#111827' : '#fff'};
    &:hover { filter: brightness(1.03); }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  `;
  
  const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
  `;